import { gql } from "lib/apolloClient";

import {
  attachmentFragment,
  userFragment,
  taggedPracticesFragment,
  toolResponseFragment,
  pypElementRatingFragment,
  resolvedFieldValueFragment,
} from "modules/CommonFragments";
import { plannerFieldFragment } from "UnitPlans/modules/UnitPlanFragments";

const postStudentResponseFragment = {
  studentResponse: gql`
    fragment studentResponseItem on StudentAssignment {
      id
      isEvaluated
      isSharedWithStudent
      student {
        id
        firstName
        middleName
        lastName
        pronouns
        preferredName
        suffix
        profileImage
      }
      submission {
        id
        response
        attachmentGroups {
          id
          attachments {
            ...attachmentItem
          }
        }
      }
      toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
      ibPYPElementRatings {
        ...ratingItem
      }
      elementRemarks(filters: { creatorTypes: [STAFF] }) {
        id
        message
        ibPYPElementId
        ibPYPElementType
      }
      assignment {
        id
        isStudentEvaluationEnabled
        isTeacherEvaluationEnabled
        content {
          ... on Assessment {
            id
            title {
              id
              value
            }
            assessmentType {
              id
              value
            }
            assessmentTools {
              edges {
                node {
                  id
                  assessmentToolItem {
                    id
                    ... on MypObjectiveRubric {
                      id
                      label
                    }
                    ... on ScoreAssessmentTool {
                      id
                      scoreToolData {
                        id
                        maxScore
                      }
                    }
                  }
                  assessmentToolType
                  isTeacherEvaluationEnable
                  isStudentEvaluationEnable
                }
              }
            }
            assessmentToolType
            fields(
              uids: [
                "lp"
                "centralIdea"
                "concepts"
                "atls"
                "benchmarks"
                "lois"
              ]
            ) {
              id
              uid
              value
              resolvedMinimalTree {
                ...resolvedMinimalTreeItem
              }
            }
            isLearningGoalsEvaluationEnabled
          }
        }
      }
      post {
        id
      }
    }
    ${toolResponseFragment.toolResponse}
    ${attachmentFragment.attachment}
    ${resolvedFieldValueFragment.resolvedMinimalTree}
    ${pypElementRatingFragment.studentElementRating}
  `,
};

export const postEvidenceFragment = {
  postEvidence: gql`
    fragment postItem on Post {
      id
      title
      attachments {
        ...attachmentItem
      }
      state
      elementPYP {
        id
        fieldUID
        value
      }
      resourceItem {
        id
        title {
          id
          value
        }
        templateId
        ... on Assessment {
          assessmentType {
            id
            value
          }
          taskType {
            id
            type
            label
          }
        }
      }
      postTaggedEntity {
        id
        entityType
        entity {
          ... on UnitPlan {
            id
          }
          ... on Assessment {
            id
            title {
              id
              value
            }
            assessmentType {
              id
              value
            }
          }
        }
      }
      createdBy {
        ...userItem
      }
      taggedPractices {
        ...taggedPracticesOnEvidence
      }
      items {
        item {
          id
          ...studentResponseItem
        }
        itemType
      }
      students {
        edges {
          node {
            id
            firstName
            middleName
            lastName
            profileImage
          }
        }
      }
    }
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${taggedPracticesFragment.taggedPractices}
    ${postStudentResponseFragment.studentResponse}
  `,
  postEvidenceFilter: gql`
    fragment postItem on Post {
      id
      title
      attachments {
        ...attachmentItem
      }

      createdBy {
        ...userItem
      }
      items {
        item {
          id
          ...studentResponseItem
        }
        itemType
      }
    }
    ${attachmentFragment.attachment}
    ${userFragment.basicUserDetails}
    ${postStudentResponseFragment.studentResponse}
  `,
};

export const unitPlanEvidenceFragment = {
  unitPlanEvidenceWithFilter: gql`
    fragment unitPlanItem on UnitPlan {
      id
      posts(filters: $filters) {
        ...postItem
      }
    }
    ${postEvidenceFragment.postEvidenceFilter}
  `,
  fields: gql`
    fragment unitPlanFields on UnitPlan {
      id
      fields(uids: $uids) {
        ...resourceLibraryFieldItem
      }
    }
    ${plannerFieldFragment.resourceLibraryField}
  `,
};
